import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

// FIKSET: La til FiUsers i import-listen for å hindre krasj
const { FiArrowLeft, FiPlus, FiSearch, FiEdit, FiTrash2, FiUser, FiUsers, FiMail, FiPhone, FiBuilding, FiShield, FiX, FiInfo, FiTag, FiBriefcase } = FiIcons;

const UserManagement = () => {
  const navigate = useNavigate();
  const { companies = [], users = [], deleteUser } = useData(); 
  const [searchTerm, setSearchTerm] = useState('');
  const [viewingUser, setViewingUser] = useState(null);

  const internalCompanyIds = ['hms_internal']; 
  const internalRoles = ['superadmin', 'dept_leader', 'sales_chief', 'team_leader', 'seller'];

  const customerUsers = Array.isArray(users) ? users
    .filter(u => {
      const isInternalRole = internalRoles.includes(u.role);
      const isInternalCompany = internalCompanyIds.includes(u.companyId);
      return !isInternalRole && !isInternalCompany;
    })
    .map(user => {
      const company = companies.find(c => c.id === user.companyId);
      return { 
        ...user, 
        companyName: company ? company.name : 'Ukjent Bedrift',
        subscription: company ? company.subscription : 'Standard'
      };
    })
    .filter(u => 
      (u.name && u.name.toLowerCase().includes(searchTerm.toLowerCase())) || 
      (u.companyName && u.companyName.toLowerCase().includes(searchTerm.toLowerCase()))
    ) : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-20 h-16 flex items-center justify-between px-8">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/superadmin')} className="p-2 hover:bg-gray-100 rounded-lg"><SafeIcon icon={FiArrowLeft} /></button>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <SafeIcon icon={FiUsers} className="text-green-600" /> Kunder & Sluttbrukere
          </h1>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Søk kunder, ansatte i bedrifter eller firmanavn..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-12 pr-4 py-3 bg-gray-50 rounded-xl" />
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-[10px] font-black uppercase text-gray-400 tracking-widest border-b">
              <tr>
                <th className="px-6 py-4">Navn</th>
                <th className="px-6 py-4">Bedrift</th>
                <th className="px-6 py-4">Rolle</th>
                <th className="px-6 py-4 text-right">Info</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {customerUsers.length > 0 ? customerUsers.map(u => (
                <tr key={u.id} className="hover:bg-blue-50/30 cursor-pointer transition-colors" onClick={() => setViewingUser(u)}>
                  <td className="px-6 py-4 font-bold text-gray-900">{u.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{u.companyName}</td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-black uppercase">{u.role}</span>
                  </td>
                  <td className="px-6 py-4 text-right"><SafeIcon icon={FiInfo} className="text-gray-300" /></td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="4" className="px-6 py-12 text-center text-gray-500">Ingen brukere funnet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AnimatePresence>
        {viewingUser && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={() => setViewingUser(null)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white w-full max-w-xl rounded-[32px] shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="bg-blue-600 p-8 text-white flex justify-between items-start">
                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-md text-3xl font-bold">{viewingUser.name.charAt(0)}</div>
                  <div>
                    <h2 className="text-3xl font-black">{viewingUser.name}</h2>
                    <p className="text-white/80 font-bold uppercase text-xs tracking-widest mt-1">Kundebruker / Ansatt</p>
                  </div>
                </div>
                <button onClick={() => setViewingUser(null)} className="p-2 hover:bg-white/20 rounded-full"><SafeIcon icon={FiX} size={24} /></button>
              </div>
              <div className="p-8 space-y-6">
                <div className="grid grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest border-b pb-2">Kontakt</h3>
                    <p className="text-sm font-bold flex items-center gap-2"><SafeIcon icon={FiMail} /> {viewingUser.email}</p>
                    <p className="text-sm font-bold flex items-center gap-2"><SafeIcon icon={FiPhone} /> {viewingUser.phone || 'Ingen tlf'}</p>
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest border-b pb-2">Bedriftsinfo</h3>
                    <p className="text-sm font-bold flex items-center gap-2"><SafeIcon icon={FiBuilding} /> {viewingUser.companyName}</p>
                    <p className="text-sm font-black text-blue-600 uppercase tracking-tighter">Abo: {viewingUser.subscription}</p>
                  </div>
                </div>
                <button onClick={() => { deleteUser(viewingUser.id); setViewingUser(null); }} className="w-full py-4 bg-red-50 text-red-600 rounded-2xl font-bold">Slett Bruker</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserManagement;